import {css} from '@emotion/css';
import {styled} from '@mui/material/styles';
import {ReactNode, useMemo} from 'react';

import {FuseSearchKey} from 'utils/hooks';
import {useTreeSelection} from 'utils/hooks/useTreeSelection';
import {filterTree, getAllLeaveKeys, GenericTreeNode} from 'utils/treeUtils';

import LoadingIndicator from 'components/LoadingIndicator';
import type {SearchProps} from 'components/Search';
import {Tree, TreeProps} from 'components/Tree/Tree';
import type {TreeBasicNode} from 'components/Tree/TreeTypes';
import {TreeKey} from 'components/Tree/TreeTypes';

import {TreePickerControls} from './TreePickerControls';

import {NothingFoundPlaceholder} from 'modules/decisionsNext/components/placeholders/NothingFoundPlaceholder';

// 6 rows maximum
const MAX_HEIGHT = 52 * 6;

export type TreePickerListBoxProps<K extends TreeKey, T extends TreeBasicNode<K>> = {
  treeData: GenericTreeNode<T>[];
  fullTreeData?: GenericTreeNode<T>[];
  loading?: boolean;
  /**
   * User types something in the input
   */
  searching: boolean;
  defaultExpandedKeys?: K[];
  disabledKeys?: Set<K> | K[];
  isDisabled?: boolean;
  isNothingFound: boolean;
  searchKeys?: FuseSearchKey<T[]>[];
  entityTitle: string;
  entitySingular?: string;
  selected?: K[];
  setSelected: (newSelected: K[]) => void;
  localSelected: K[];
  setLocalSelected: (newSelected: K[]) => void;
  onSearchChange?: SearchProps['onChange'];
  selectionCount?: number | string;
  getLocalSelectedLabel?: (selected: K[]) => ReactNode;
  additionalFilters?: ReactNode;
  handleClose: () => void;
  clearSearch?: () => void;
} & Omit<TreeProps<K, T>, 'treeData' | 'checkedKeys' | 'onCheck'>;

const sTree = css`
  && {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    & .rc-tree {
      border: none;
    }

    & .rc-tree-treenode {
      border-left: none !important;
      border-right: none !important;
    }

    & .rc-tree-treenode:first-of-type {
      border-top: none !important;
      border-top-left-radius: 8px !important;
      border-top-right-radius: 8px !important;
    }

    & .rc-tree-treenode:last-of-type {
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
  }
`;

const SCentered = styled('div')({
  height: MAX_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export function TreePickerBox<K extends TreeKey, T extends TreeBasicNode<K>>({
  treeData,
  fullTreeData = treeData,
  loading,
  searching,
  setSelected,
  localSelected,
  setLocalSelected,
  isNothingFound,
  handleClose,
  clearSearch,
  getLocalSelectedLabel,
  ...props
}: TreePickerListBoxProps<K, T>) {
  const {onSelectAll, isSelectAllDisabled, onSelectNone, onCheck} = useTreeSelection({
    treeData: fullTreeData,
    filteredTreeData: useMemo(() => {
      if (!props.disabledKeys) {
        return treeData;
      }

      const disabledSet = Array.isArray(props.disabledKeys) ? new Set(props.disabledKeys) : props.disabledKeys;

      return filterTree(treeData, item => !disabledSet.has(item.key));
    }, [treeData, props.disabledKeys]),
    selected: localSelected,
    setSelected: setLocalSelected,
  });

  const onClear = () => {
    if (searching) {
      clearSearch?.();
    }

    const newSelected = onSelectNone();
    setSelected(newSelected);
  };

  const onApply = () => {
    setSelected(localSelected);
    handleClose();
  };

  const visibleCheckedKeys = useMemo(() => {
    const visibleKeys = new Set(getAllLeaveKeys<K, T>(treeData));
    return localSelected.filter(key => visibleKeys.has(key));
  }, [treeData, localSelected]);

  const renderContent = () => {
    if (loading) {
      return (
        <SCentered>
          <LoadingIndicator />
        </SCentered>
      );
    }

    if (isNothingFound) {
      return (
        <SCentered>
          <NothingFoundPlaceholder />
        </SCentered>
      );
    }

    return (
      <Tree
        {...props}
        treeData={treeData}
        height={MAX_HEIGHT}
        checkedKeys={visibleCheckedKeys}
        onCheck={onCheck}
        className={sTree}
      />
    );
  };

  return (
    <>
      {renderContent()}
      <TreePickerControls
        selectionCount={localSelected.length || 'All'}
        isSelectAllDisabled={isSelectAllDisabled}
        onSelectAll={onSelectAll}
        onSelectNone={onClear}
        hideSelectAll={searching}
        onApply={onApply}
        selectionLabel={getLocalSelectedLabel?.(localSelected)}
      />
    </>
  );
}
